/* Buyer chosen location flow shared by home, marketplace and cart.
 * Geolocation is only called from its button. Map code and OSM tiles are only
 * loaded after the buyer opens the map and has seen the disclosure in the dialog.
 */
(function (root) {
  'use strict';

  var SELECTION_KEY = 'samaanbox:buyer-location-selection:v1';
  var SEEN_KEY = 'samaanbox:buyer-location-dialog-seen:v1';
  var AREA_KEY = 'samaanbox:location-feed:area:v1';
  var POINT_KEY = 'samaanbox:buyer-location:v1';
  var BUYER_LOCATION_TTL = 30 * 24 * 60 * 60 * 1000;
  var LEAFLET_VERSION = '1.9.4';
  var MAX_LABEL_LENGTH = 180;
  var dialog = null;
  var status = null;
  var mapStatus = null;
  var queryInput = null;
  var resultsSelect = null;
  var resultsLabel = null;
  var geolocateButton = null;
  var openMapButton = null;
  var mapContainer = null;
  var confirmButton = null;
  var forgetButton = null;
  var areaSelect = null;
  var areaForm = null;
  var areaLoadButton = null;
  var manualForm = null;
  var manualLatitude = null;
  var manualLongitude = null;
  var draft = null;
  var draftMode = '';
  var draftLabel = '';
  var searchResults = [];
  var searchPending = false;
  var areasPending = false;
  var mapLoading = null;
  var mapInstance = null;
  var mapMarker = null;
  var mapHasTiles = false;
  var mapTilesUnavailable = false;
  var pinRefined = false;
  var lastOpener = null;

  function safeStorage() {
    try { return root.sessionStorage || null; } catch (error) { return null; }
  }

  function cleanLabel(value) {
    if (typeof value !== 'string') return '';
    var label = value.trim();
    return label && label.length <= MAX_LABEL_LENGTH && !/[\u0000-\u001f\u007f]/.test(label) ? label : '';
  }

  function safeLocalStorage() {
    try { return root.localStorage || null; } catch (error) { return null; }
  }

  function readSavedLocation() {
    var storage = safeLocalStorage();
    if (!storage) return null;
    var raw;
    try { raw = storage.getItem(POINT_KEY); } catch (error) { return null; }
    if (!raw) return null;
    var value;
    try { value = JSON.parse(raw); } catch (error) { value = null; }
    var now = Date.now();
    var valid = value && typeof value === 'object' && Number.isFinite(value.savedAt) && value.savedAt > 0 &&
      value.savedAt <= now && now - value.savedAt <= BUYER_LOCATION_TTL;
    var selection = null;
    if (valid && value.method === 'area') {
      var areaLabel = cleanLabel(value.areaLabel);
      if (areaLabel) selection = { mode: 'area', label: areaLabel };
    } else if (valid && ['geolocation', 'search', 'map', 'manual'].indexOf(value.method) !== -1 &&
      Number.isFinite(value.latitude) && Number.isFinite(value.longitude)) {
      var point = validPoint(value);
      if (point) selection = { mode: 'point', latitude: point.latitude, longitude: point.longitude, label: cleanLabel(value.areaLabel) || '' };
    }
    if (!selection) {
      try { storage.removeItem(POINT_KEY); } catch (error) { /* optional cache */ }
    }
    return selection;
  }

  function writeSavedLocation(selection, method) {
    var storage = safeLocalStorage();
    if (!storage || !selection) return false;
    var record;
    if (selection.mode === 'area') {
      var areaLabel = cleanLabel(selection.label);
      if (!areaLabel) return false;
      record = { latitude: null, longitude: null, method: 'area', areaLabel: areaLabel, savedAt: Date.now() };
    } else {
      var point = validPoint(selection);
      if (!point || ['geolocation', 'search', 'map', 'manual'].indexOf(method) === -1) return false;
      record = { latitude: point.latitude, longitude: point.longitude, method: method, savedAt: Date.now() };
    }
    try {
      storage.setItem(POINT_KEY, JSON.stringify(record));
      return true;
    } catch (error) { return false; }
  }

  function forgetSavedLocation() {
    var local = safeLocalStorage();
    if (local) {
      try { local.removeItem(POINT_KEY); } catch (error) { /* optional */ }
    }
    var session = safeStorage();
    if (session) {
      [SELECTION_KEY, SEEN_KEY, AREA_KEY, POINT_KEY].forEach(function (key) {
        try { session.removeItem(key); } catch (error) { /* optional */ }
      });
    }
    if (root.SamaanBoxCartContext && typeof root.SamaanBoxCartContext.rememberPoint === 'function') {
      root.SamaanBoxCartContext.rememberPoint(null);
    }
    draft = null;
    draftMode = '';
    draftLabel = '';
    pinRefined = false;
    setConfirmEnabled(false);
    updateHeader(null);
    setStatus(text('label-prompt'), false);
  }

  function validPoint(value) {
    if (!value || typeof value !== 'object') return null;
    var latitude = Number(value.latitude);
    var longitude = Number(value.longitude);
    if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) return null;
    if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) return null;
    return { latitude: latitude, longitude: longitude };
  }

  function storedSelection() {
    var savedLocation = readSavedLocation();
    if (savedLocation) return savedLocation;
    var storage = safeStorage();
    if (!storage) return null;
    try {
      var rawSelection = storage.getItem(SELECTION_KEY);
      if (!rawSelection) {
        var previousArea = cleanLabel(storage.getItem(AREA_KEY));
        if (previousArea) return { mode: 'area', label: previousArea };
        var previousPoint = root.SamaanBoxCartContext && typeof root.SamaanBoxCartContext.readPoint === 'function'
          ? validPoint(root.SamaanBoxCartContext.readPoint())
          : null;
        if (!previousPoint) {
          try { previousPoint = validPoint(JSON.parse(storage.getItem(POINT_KEY) || 'null')); } catch (error) { previousPoint = null; }
        }
        if (previousPoint) return { mode: 'point', latitude: previousPoint.latitude, longitude: previousPoint.longitude, label: '' };
      }
      var value = JSON.parse(rawSelection || 'null');
      if (!value || typeof value !== 'object') return null;
      if (value.mode === 'area') {
        var area = cleanLabel(value.label);
        return area ? { mode: 'area', label: area } : null;
      }
      if (value.mode === 'point') {
        var point = validPoint(value);
        if (!point) return null;
        return { mode: 'point', latitude: point.latitude, longitude: point.longitude, label: cleanLabel(value.label) || '' };
      }
      return null;
    } catch (error) { return null; }
  }

  function storeSelection(selection) {
    var storage = safeStorage();
    if (!storage) return false;
    try {
      storage.setItem(SELECTION_KEY, JSON.stringify(selection));
      storage.setItem(SEEN_KEY, '1');
      if (selection.mode === 'area') storage.setItem(AREA_KEY, selection.label);
      else storage.removeItem(AREA_KEY);
      return true;
    } catch (error) { return false; }
  }

  function text(key, fallback) {
    return (dialog && dialog.getAttribute('data-' + key)) || fallback || '';
  }

  function updateHeader(selection) {
    if (forgetButton) forgetButton.hidden = !selection;
    var value = '';
    if (selection && selection.mode === 'area') value = selection.label;
    else if (selection && selection.mode === 'point') value = cleanLabel(selection.label) || text('label-pinned');
    else value = text('label-prompt');
    document.querySelectorAll('[data-buyer-location-current]').forEach(function (node) {
      node.textContent = value;
    });
  }

  function setStatus(message, isError) {
    if (!status) return;
    status.textContent = message || '';
    status.setAttribute('role', isError ? 'alert' : 'status');
  }

  function setMapStatus(message, isError) {
    if (!mapStatus) return;
    mapStatus.textContent = message || '';
    mapStatus.setAttribute('role', isError ? 'alert' : 'status');
  }

  function setConfirmEnabled(enabled) {
    if (confirmButton) confirmButton.disabled = !enabled;
  }

  function setBusy(button, busy) {
    if (!button) return;
    button.disabled = !!busy;
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  function endpoint(name, fallback) {
    var feed = document.querySelector('[data-location-feed]');
    var attr = name === 'search' ? 'data-location-search-endpoint' :
      name === 'areas' ? 'data-areas-endpoint' : 'data-endpoint';
    var configured = feed && feed.getAttribute(attr);
    return configured || (dialog && dialog.getAttribute('data-' + name + '-endpoint')) || fallback || '';
  }

  function classifyStatus(code) {
    if (code === 429) return text('rate-limited');
    if (code === 0 || !root.navigator || root.navigator.onLine === false) return text('offline');
    return '';
  }

  function makeUrl(path, query) {
    if (!path) return '';
    try {
      var url = new URL(path, root.location.origin);
      if (query !== undefined) url.searchParams.set('q', query);
      return url.toString();
    } catch (error) { return ''; }
  }

  function fetchJson(url, options) {
    return root.fetch(url, Object.assign({
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }, options || {})).then(function (response) {
      if (!response || response.status < 200 || response.status >= 300) {
        var message = classifyStatus(response && response.status);
        var failure = new Error(message || text('search-unavailable'));
        failure.status = response && response.status;
        throw failure;
      }
      return response.json();
    });
  }

  function showError(error, fallback) {
    var message = error && error.message;
    if ((root.navigator && root.navigator.onLine === false) || /failed to fetch|networkerror|network error/i.test(message || '')) {
      message = text('offline');
    } else if (error && error.status === 429) {
      message = text('rate-limited');
    }
    setStatus(message && message !== 'Failed to fetch' ? message : (fallback || text('search-unavailable')), true);
  }

  function dispatchSelection(selection) {
    document.dispatchEvent(new CustomEvent('samaanbox:location-confirmed', { detail: selection }));
  }

  function markSeen() {
    var storage = safeStorage();
    if (storage) {
      try { storage.setItem(SEEN_KEY, '1'); } catch (error) { /* optional */ }
    }
  }

  function openDialog(opener) {
    if (!dialog) return;
    lastOpener = opener || document.activeElement;
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', 'open');
    }
    markSeen();
    var panel = dialog.querySelector('.samaanbox-location-dialog__panel');
    dialog.scrollTop = 0;
    if (panel) panel.scrollTop = 0;
    setTimeout(function () {
      if (!dialog.open) return;
      var title = dialog.querySelector('#BuyerLocationTitle');
      var target = title || dialog.querySelector('[data-buyer-location-close]');
      dialog.scrollTop = 0;
      if (panel) panel.scrollTop = 0;
      if (target) {
        try { target.focus({ preventScroll: true }); }
        catch (error) { target.focus(); }
      }
    }, 0);
  }

  function closeDialog() {
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
  }

  function dialogClose() {
    if (lastOpener && typeof lastOpener.focus === 'function' && document.contains(lastOpener)) lastOpener.focus();
  }

  function currentMapPoint() {
    if (draft) return [draft.latitude, draft.longitude];
    if (mapInstance) {
      var center = mapInstance.getCenter();
      if (center) return [center.lat, center.lng];
    }
    // A neutral national overview is only the initial map viewport; no marker
    // or delivery coverage is implied until the buyer drops and confirms a pin.
    return [30.3753, 69.3451];
  }

  function putMarker(point, draggable) {
    if (!mapInstance || !root.L) return;
    if (mapMarker) mapInstance.removeLayer(mapMarker);
    mapMarker = root.L.marker([point.latitude, point.longitude], {
      draggable: draggable !== false,
      keyboard: true,
      title: text('map-confirm-ready'),
      alt: text('map-confirm-ready')
    }).addTo(mapInstance);
    mapMarker.on('dragend', function () {
      var at = mapMarker.getLatLng();
      applyMapPoint(at.lat, at.lng, true);
    });
  }

  function setDraft(point, mode, label, refined) {
    draft = validPoint(point);
    if (!draft) {
      draftMode = '';
      draftLabel = '';
      pinRefined = false;
      setConfirmEnabled(false);
      return;
    }
    draftMode = mode || 'map';
    draftLabel = cleanLabel(label);
    pinRefined = !!refined;
    if (confirmButton) {
      confirmButton.textContent = draftMode === 'search' && !pinRefined
        ? text('map-confirm-search')
        : draftMode === 'geo'
          ? text('map-confirm-current')
          : draftMode === 'manual'
            ? text('map-confirm-manual')
            : text('map-confirm-ready');
    }
    setConfirmEnabled(draftMode !== 'search' || pinRefined);
  }

  function applyMapPoint(latitude, longitude, refined) {
    if (mapTilesUnavailable && !mapHasTiles) {
      setMapStatus(text('map-unavailable'), true);
      return;
    }
    var point = validPoint({ latitude: latitude, longitude: longitude });
    if (!point) return;
    var mode = draftMode === 'search' ? 'search' : 'map';
    setDraft(point, mode, draftLabel, refined);
    putMarker(point, true);
    if (refined) {
      setMapStatus(text('map-confirm-ready'), false);
      setStatus(text('map-confirm-ready'), false);
    }
  }

  function loadLeaflet() {
    if (root.L && typeof root.L.map === 'function') return Promise.resolve(root.L);
    if (mapLoading) return mapLoading;
    mapLoading = new Promise(function (resolve, reject) {
      var base = 'https://cdn.jsdelivr.net/npm/leaflet@' + LEAFLET_VERSION + '/dist/leaflet';
      var link = document.querySelector('link[data-samaanbox-leaflet-css]');
      var newLink = false;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = base + '.css';
        link.setAttribute('data-samaanbox-leaflet-css', '');
        newLink = true;
      }
      var done = false;
      var timer = setTimeout(function () {
        if (!done) { done = true; reject(new Error('leaflet_unavailable')); }
      }, 12000);
      function fail() {
        if (done) return;
        done = true;
        clearTimeout(timer);
        reject(new Error('leaflet_unavailable'));
      }
      function loadScript() {
        if (done) return;
        if (root.L && typeof root.L.map === 'function') {
          done = true;
          clearTimeout(timer);
          resolve(root.L);
          return;
        }
        var script = document.querySelector('script[data-samaanbox-leaflet-js]');
        if (!script) {
          script = document.createElement('script');
          script.src = base + '.js';
          script.async = true;
          script.setAttribute('data-samaanbox-leaflet-js', '');
          document.head.appendChild(script);
        }
        script.addEventListener('load', function () {
          if (done) return;
          if (root.L && typeof root.L.map === 'function') {
            done = true;
            clearTimeout(timer);
            resolve(root.L);
          } else fail();
        }, { once: true });
        script.addEventListener('error', fail, { once: true });
      }
      if (link.getAttribute('data-samaanbox-loaded') === 'true' || (!newLink && link.sheet)) loadScript();
      else {
        link.addEventListener('load', function () {
          link.setAttribute('data-samaanbox-loaded', 'true');
          loadScript();
        }, { once: true });
        link.addEventListener('error', fail, { once: true });
        if (newLink) document.head.appendChild(link);
      }
    }).catch(function (error) {
      var failedScript = document.querySelector('script[data-samaanbox-leaflet-js]');
      if (failedScript && !root.L) failedScript.remove();
      var failedCss = document.querySelector('link[data-samaanbox-leaflet-css]');
      if (failedCss && !failedCss.sheet) failedCss.remove();
      mapLoading = null;
      throw error;
    });
    return mapLoading;
  }

  function initMap(L) {
    if (mapInstance) return mapInstance;
    mapInstance = L.map(mapContainer, {
      zoomControl: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      touchZoom: true,
      keyboard: true,
      tap: true,
      zoomAnimation: false,
      updateWhenIdle: true
    }).setView(currentMapPoint(), draftMode === 'search' ? 14 : draft ? 15 : 5);
    var tiles = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      maxNativeZoom: 19,
      updateWhenIdle: true,
      keepBuffer: 1,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap contributors</a>'
    }).addTo(mapInstance);
    mapHasTiles = false;
    mapTilesUnavailable = false;
    tiles.once('load', function () {
      mapHasTiles = true;
      mapTilesUnavailable = false;
      if (mapStatus && mapStatus.textContent === text('map-unavailable')) setMapStatus('', false);
    });
    tiles.on('tileerror', function () {
      if (!mapHasTiles) {
        mapTilesUnavailable = true;
        if (draftMode === 'search' || draftMode === 'map') setConfirmEnabled(false);
        setMapStatus(text('map-unavailable'), true);
      }
    });
    mapInstance.on('click', function (event) {
      applyMapPoint(event.latlng.lat, event.latlng.lng, true);
    });
    if (draft) putMarker(draft, true);
    return mapInstance;
  }

  function openMap() {
    if (!mapContainer) return;
    mapContainer.hidden = false;
    setMapStatus(text('map-loading'), false);
    setBusy(openMapButton, true);
    loadLeaflet().then(function (L) {
      var map = initMap(L);
      setTimeout(function () {
        map.invalidateSize(true);
        if (draft) map.setView([draft.latitude, draft.longitude], draftMode === 'search' ? 15 : Math.max(map.getZoom(), 15));
      }, 80);
      setBusy(openMapButton, false);
      if (draftMode === 'search' && !pinRefined) setMapStatus(text('map-refine'), false);
      else if (draft) setMapStatus(text('map-confirm-ready'), false);
      else setMapStatus('', false);
    }).catch(function () {
      setBusy(openMapButton, false);
      setMapStatus(text('map-unavailable'), true);
      setStatus(text('map-unavailable'), true);
    });
  }

  function searchPlaces(event) {
    if (event && event.preventDefault) event.preventDefault();
    if (searchPending) return;
    var query = queryInput ? String(queryInput.value || '').trim() : '';
    if (!query) {
      setStatus(text('search-required'), true);
      if (queryInput) queryInput.focus();
      return;
    }
    if (query.length > 160) {
      setStatus(text('search-required'), true);
      return;
    }
    var url = makeUrl(endpoint('search', '/apps/samaanbox/api/v1/location/search'), query);
    if (!url) {
      setStatus(text('search-unavailable'), true);
      return;
    }
    searchPending = true;
    setBusy(document.querySelector('[data-buyer-location-search-submit]'), true);
    setStatus(text('searching'), false);
    fetchJson(url).then(function (payload) {
      searchPending = false;
      setBusy(document.querySelector('[data-buyer-location-search-submit]'), false);
      searchResults = [];
      if (!payload || !Array.isArray(payload.results)) throw new Error(text('search-unavailable'));
      payload.results.slice(0, 8).forEach(function (item) {
        var label = cleanLabel(item && item.label);
        var point = validPoint(item);
        if (label && point) searchResults.push({ label: label, latitude: point.latitude, longitude: point.longitude });
      });
      if (!searchResults.length) {
        if (resultsSelect) resultsSelect.hidden = true;
        if (resultsLabel) resultsLabel.hidden = true;
        setStatus(text('search-empty'), false);
        return;
      }
      while (resultsSelect.firstChild) resultsSelect.removeChild(resultsSelect.firstChild);
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = text('area-choose');
      resultsSelect.appendChild(placeholder);
      searchResults.forEach(function (item, index) {
        var option = document.createElement('option');
        option.value = String(index);
        option.textContent = item.label;
        resultsSelect.appendChild(option);
      });
      resultsSelect.hidden = false;
      resultsLabel.hidden = false;
      setStatus(text('map-refine'), false);
      resultsSelect.focus();
    }).catch(function (error) {
      searchPending = false;
      setBusy(document.querySelector('[data-buyer-location-search-submit]'), false);
      showError(error, text('search-unavailable'));
    });
  }

  function selectSearchResult() {
    var index = resultsSelect ? Number(resultsSelect.value) : -1;
    var selected = Number.isInteger(index) && index >= 0 ? searchResults[index] : null;
    if (!selected) {
      setDraft(null, '', '', false);
      setStatus(text('search-required'), true);
      return;
    }
    setDraft(selected, 'search', selected.label, false);
    setMapStatus(text('map-refine'), false);
    setStatus(text('map-refine'), false);
    if (openMapButton) openMapButton.focus();
  }

  function askForBrowserLocation() {
    if (!root.navigator || !root.navigator.geolocation) {
      setStatus(text('geo-unavailable'), true);
      return;
    }
    setBusy(geolocateButton, true);
    setStatus(text('geo-requesting'), false);
    root.navigator.geolocation.getCurrentPosition(function (position) {
      setBusy(geolocateButton, false);
      var point = validPoint({ latitude: position.coords.latitude, longitude: position.coords.longitude });
      if (!point) {
        setStatus(text('geo-unavailable'), true);
        return;
      }
      setDraft(point, 'geo', text('label-current'), true);
      setStatus(text('map-confirm-current'), false);
      if (mapInstance) {
        mapInstance.setView([point.latitude, point.longitude], 15);
        putMarker(point, true);
      }
    }, function (error) {
      setBusy(geolocateButton, false);
      var message = error && error.code === 1 ? text('geo-denied') : error && error.code === 3 ? text('geo-timeout') : text('geo-unavailable');
      setStatus(message, true);
    }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
  }

  function submitManual(event) {
    if (event && event.preventDefault) event.preventDefault();
    var point = validPoint({ latitude: manualLatitude && manualLatitude.value, longitude: manualLongitude && manualLongitude.value });
    if (!point || String(manualLatitude.value).trim() === '' || String(manualLongitude.value).trim() === '') {
      setStatus(text('manual-invalid'), true);
      if (manualLatitude && String(manualLatitude.value).trim() === '') manualLatitude.focus();
      else if (manualLongitude) manualLongitude.focus();
      return;
    }
    setDraft(point, 'manual', text('label-pinned'), true);
    setStatus(text('map-confirm-manual'), false);
  }

  function populateAreas(labels) {
    while (areaSelect.firstChild) areaSelect.removeChild(areaSelect.firstChild);
    var placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = text('area-choose');
    areaSelect.appendChild(placeholder);
    labels.slice(0, 50).forEach(function (value) {
      var label = cleanLabel(value);
      if (!label) return;
      var option = document.createElement('option');
      option.value = label;
      option.textContent = label;
      areaSelect.appendChild(option);
    });
  }

  function loadAreas() {
    if (areasPending) return;
    var url = endpoint('areas', '/apps/samaanbox/api/v1/feed/areas');
    if (!url) {
      setStatus(text('area-unavailable'), true);
      return;
    }
    areasPending = true;
    setBusy(areaLoadButton, true);
    setStatus(text('areas-loading'), false);
    fetchJson(url).then(function (payload) {
      areasPending = false;
      setBusy(areaLoadButton, false);
      var labels = payload && Array.isArray(payload.areas) ? payload.areas : null;
      if (!labels) throw new Error(text('area-unavailable'));
      var safeLabels = [];
      labels.forEach(function (value) {
        var clean = cleanLabel(value);
        if (clean && safeLabels.indexOf(clean) < 0) safeLabels.push(clean);
      });
      if (!safeLabels.length) {
        setStatus(text('areas-empty'), false);
        return;
      }
      populateAreas(safeLabels);
      areaForm.hidden = false;
      var previous = storedSelection();
      if (previous && previous.mode === 'area' && safeLabels.indexOf(previous.label) >= 0) areaSelect.value = previous.label;
      setStatus('', false);
      areaSelect.focus();
    }).catch(function (error) {
      areasPending = false;
      setBusy(areaLoadButton, false);
      showError(error, text('area-unavailable'));
    });
  }

  function submitArea(event) {
    if (event && event.preventDefault) event.preventDefault();
    var label = cleanLabel(areaSelect && areaSelect.value);
    if (!label) {
      setStatus(text('area-required'), true);
      if (areaSelect) areaSelect.focus();
      return;
    }
    commitSelection({ mode: 'area', label: label });
  }

  function commitSelection(selection) {
    var safe;
    if (selection.mode === 'area') {
      var area = cleanLabel(selection.label);
      if (!area) return;
      safe = { mode: 'area', label: area, method: 'area' };
    } else {
      var point = validPoint(selection);
      if (!point) return;
      var method = draftMode === 'geo' ? 'geolocation' :
        draftMode === 'search' ? 'search' :
          draftMode === 'manual' ? 'manual' : 'map';
      safe = { mode: 'point', latitude: point.latitude, longitude: point.longitude, label: cleanLabel(selection.label) || text('label-pinned'), method: method };
    }
    storeSelection(safe);
    writeSavedLocation(safe, safe.method);
    updateHeader(safe);
    markSeen();
    setConfirmEnabled(false);
    setStatus(text('feed-loading'), false);
    closeDialog();

    var changed = root.SamaanBoxCartContext && typeof root.SamaanBoxCartContext.locationChanged === 'function'
      ? root.SamaanBoxCartContext.locationChanged(safe.mode === 'point' ? safe : null)
      : Promise.resolve();
    Promise.resolve(changed).catch(function () { /* checkout guard remains fail-closed */ }).then(function () {
      dispatchSelection(safe);
      setTimeout(function () {
        var feedStatus = document.querySelector('[data-location-feed-status]');
        if (feedStatus && feedStatus.textContent) return;
        var statusNode = document.querySelector('[data-buyer-location-status]');
        if (statusNode) statusNode.textContent = '';
      }, 0);
    });
  }

  function confirmPoint() {
    if (!draft || (draftMode === 'search' && !pinRefined)) return;
    var label = draftMode === 'search'
      ? text('label-near') + ' ' + draftLabel
      : draftMode === 'geo'
        ? text('label-current')
        : text('label-pinned');
    commitSelection({ mode: 'point', latitude: draft.latitude, longitude: draft.longitude, label: label });
  }

  function initialize() {
    dialog = document.querySelector('[data-buyer-location-dialog]');
    if (!dialog) return;
    status = dialog.querySelector('[data-buyer-location-status]');
    mapStatus = dialog.querySelector('[data-buyer-location-map-status]');
    queryInput = dialog.querySelector('[data-buyer-location-query]');
    resultsSelect = dialog.querySelector('[data-buyer-location-results]');
    resultsLabel = dialog.querySelector('[data-buyer-location-results-label]');
    geolocateButton = dialog.querySelector('[data-buyer-location-geolocate]');
    openMapButton = dialog.querySelector('[data-buyer-location-open-map]');
    mapContainer = dialog.querySelector('[data-buyer-location-map]');
    confirmButton = dialog.querySelector('[data-buyer-location-confirm]');
    forgetButton = dialog.querySelector('[data-buyer-location-forget]');
    areaSelect = dialog.querySelector('[data-buyer-location-area]');
    areaForm = dialog.querySelector('[data-buyer-location-area-form]');
    areaLoadButton = dialog.querySelector('[data-buyer-location-load-areas]');
    manualForm = dialog.querySelector('[data-buyer-location-manual-form]');
    manualLatitude = dialog.querySelector('[data-buyer-location-latitude]');
    manualLongitude = dialog.querySelector('[data-buyer-location-longitude]');

    document.querySelectorAll('[data-buyer-location-open]').forEach(function (button) {
      button.addEventListener('click', function () { openDialog(button); });
    });
    dialog.querySelectorAll('[data-buyer-location-close]').forEach(function (button) {
      button.addEventListener('click', closeDialog);
    });
    dialog.addEventListener('close', dialogClose);
    dialog.addEventListener('cancel', function () { markSeen(); });
    var searchForm = dialog.querySelector('[data-buyer-location-search-form]');
    if (searchForm) searchForm.addEventListener('submit', searchPlaces);
    if (resultsSelect) resultsSelect.addEventListener('change', selectSearchResult);
    if (geolocateButton) geolocateButton.addEventListener('click', askForBrowserLocation);
    if (openMapButton) openMapButton.addEventListener('click', openMap);
    if (manualForm) manualForm.addEventListener('submit', submitManual);
    if (areaLoadButton) areaLoadButton.addEventListener('click', loadAreas);
    if (areaForm) areaForm.addEventListener('submit', submitArea);
    if (confirmButton) confirmButton.addEventListener('click', confirmPoint);
    if (forgetButton) forgetButton.addEventListener('click', forgetSavedLocation);

    var selection = storedSelection();
    updateHeader(selection);
  }

  root.SamaanBoxBuyerLocation = {
    open: openDialog,
    current: storedSelection,
    keys: { selection: SELECTION_KEY, seen: SEEN_KEY, point: POINT_KEY, area: AREA_KEY }
  };

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
    else initialize();
  }
})(window);
